import React from 'react';
import Icon from '../../components/Icon';
import './TransportToggle.css';

const DEFAULT_LAN_TOOLTIP =
  'LAN — direct, fastest, no server stores your file';
const DEFAULT_CLOUD_TOOLTIP =
  'Cloud — persists across sessions, anyone in the room can fetch later';

/**
 * F13.1 — LAN ↔ Cloud transport toggle.
 *
 * Controlled segment with two pill buttons. Matches `design/sharing-screens.jsx`
 * `Transport` markup so the design preview and the live app render identically.
 *
 * Props:
 *  - `value`          — `'lan' | 'cloud'`, currently active transport.
 *  - `onChange(next)` — fires when the user taps the non-active button.
 *  - `disabled`       — when true, the LAN button is greyed + non-interactive
 *                       and Cloud is the only valid pick. This is the "no peer
 *                       reachable on LAN" state — F10.2 wires this in once
 *                       same-LAN detection lands.
 *  - `tooltipLan` / `tooltipCloud` — override the default explainer strings.
 *    Rendered via the native `title` attribute (sufficient for v1; a richer
 *    tooltip primitive is a future enhancement).
 */
const TransportToggle = ({
  value,
  onChange,
  disabled = false,
  tooltipLan = DEFAULT_LAN_TOOLTIP,
  tooltipCloud = DEFAULT_CLOUD_TOOLTIP,
}) => {
  // When LAN is unavailable, force-display Cloud as active regardless of the
  // incoming `value` so the visual state always matches what a click would
  // actually do (the parent will treat any pick as Cloud).
  const effective = disabled ? 'cloud' : value;
  const isLan = effective === 'lan';
  const isCloud = effective === 'cloud';

  const handleClick = (next) => {
    if (typeof onChange !== 'function') return;
    if (disabled && next === 'lan') return;
    if (next === effective) return;
    onChange(next);
  };

  return (
    <div className="transport" role="group" aria-label="Transport">
      <button
        type="button"
        className={'lan ' + (isLan ? 'active' : '')}
        onClick={() => handleClick('lan')}
        disabled={disabled}
        aria-pressed={isLan}
        aria-label="LAN"
        title={tooltipLan}
      >
        <Icon
          name="wifi"
          size={14}
          stroke={isLan ? '#06371f' : 'rgba(255,255,255,.7)'}
        />
        LAN
      </button>
      <button
        type="button"
        className={'cloud ' + (isCloud ? 'active' : '')}
        onClick={() => handleClick('cloud')}
        aria-pressed={isCloud}
        aria-label="Cloud"
        title={tooltipCloud}
      >
        <Icon
          name="cloud"
          size={14}
          stroke={isCloud ? '#3a2200' : 'rgba(255,255,255,.7)'}
        />
        Cloud
      </button>
    </div>
  );
};

export default TransportToggle;
